import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  usersList: [],
  totalCount: 0,
  loading: false,
  listError: null,

  supervisorsList: [],
  supervisorsPage: 1,
  supervisorsHasMore: true,
  supervisorsTotalCount: 0,
  supervisorsLoading: false,
  supervisorsError: null,

  filters: {
    searchTerm: "",
    activeFilter: "Active",
  },

  successMessage: null,

  actionStatus: {
    blocking: false,
    unblocking: false,
    resettingPassword: false,
    creating: false,
    updating: false,
    checkingUsername: false,
    fetchingCurrentUser: false,
    fetchingTemplates: false,
    fetchingUserDetail: false,
  },
  actionErrors: {
    block: null,
    unblock: null,
    resetPassword: null,
    create: null,
    update: null,
    currentUser: null,
    templates: null,
    userDetail: null,
  },

  usernameCheck: {
    available: null,
    message: null,
    error: null,
  },

  currentUser: null,
  templates: null,
  userDetail: null,

  workload: {
    workkards: [],
    workkardsCount: 0,
    activities: [],
    activitiesCount: 0,
    selectedUser: null,
    activeTab: 'workkards',
    loading: false,
    transferring: false,
    error: null,
    transferError: null,
  },
};

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    fetchUsersListRequest: (state, action) => {
      state.loading = true;
      state.listError = null;
    },
    fetchUsersListSuccess: (state, action) => {
      state.usersList = action.payload.users;
      state.totalCount = action.payload.count;
      state.loading = false;
      state.listError = null;
    },
    fetchUsersListError: (state, action) => {
      state.listError = action.payload;
      state.loading = false;
    },

    fetchSupervisorsListRequest: (state, action) => {
      state.supervisorsLoading = true;
      state.supervisorsError = null;
      // Reset list when fetching page 1
      const page = action.payload?.page ?? 1;
      if (page === 1) {
        state.supervisorsList = [];
        state.supervisorsPage = 1;
        state.supervisorsHasMore = true;
      }
    },
    fetchSupervisorsListSuccess: (state, action) => {
      const { users, count, page } = action.payload;
      // Append users if page > 1, otherwise replace
      if (page > 1) {
        state.supervisorsList = [...state.supervisorsList, ...users];
      } else {
        state.supervisorsList = users;
      }
      state.supervisorsPage = page;
      state.supervisorsTotalCount = count;
      state.supervisorsHasMore = state.supervisorsList.length < count;
      state.supervisorsLoading = false;
      state.supervisorsError = null;
    },
    fetchSupervisorsListError: (state, action) => {
      state.supervisorsError = action.payload;
      state.supervisorsLoading = false;
    },
    resetSupervisorsList: (state) => {
      state.supervisorsList = [];
      state.supervisorsPage = 1;
      state.supervisorsHasMore = true;
      state.supervisorsTotalCount = 0;
      state.supervisorsError = null;
    },

    setSearchTerm: (state, action) => {
      state.filters.searchTerm = action.payload;
    },
    setActiveFilter: (state, action) => {
      state.filters.activeFilter = action.payload;
    },
    clearFilters: (state) => {
      state.filters.searchTerm = "";
      state.filters.activeFilter = "Active";
    },

    clearError: (state) => {
      state.listError = null;
      Object.keys(state.actionErrors).forEach((key) => {
        state.actionErrors[key] = null;
      });
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },

    blockUserRequest: (state) => {
      state.actionStatus.blocking = true;
      state.actionErrors.block = null;
    },
    blockUserSuccess: (state, action) => {
      const userId = action.payload;
      const user = state.usersList.find((u) => u.id === userId);
      if (user) {
        user.isBlocked = true;
        user.isActive = false;
      }
      state.actionStatus.blocking = false;
      state.successMessage = "User blocked successfully";
    },
    blockUserError: (state, action) => {
      state.actionErrors.block = action.payload;
      state.actionStatus.blocking = false;
    },

    unblockUserRequest: (state) => {
      state.actionStatus.unblocking = true;
      state.actionErrors.unblock = null;
    },
    unblockUserSuccess: (state, action) => {
      const userId = action.payload?.userId ?? action.payload;
      const user = state.usersList.find((u) => u.id === userId);
      if (user) {
        user.isBlocked = false;
        user.isActive = true;
      }
      state.actionStatus.unblocking = false;
      state.successMessage = "User unblocked successfully";
    },
    unblockUserError: (state, action) => {
      state.actionErrors.unblock = action.payload;
      state.actionStatus.unblocking = false;
    },

    resetUserPasswordRequest: (state) => {
      state.actionStatus.resettingPassword = true;
      state.actionErrors.resetPassword = null;
    },
    resetUserPasswordSuccess: (state, action) => {
      state.actionStatus.resettingPassword = false;
      state.successMessage =
        action.payload?.message || "Password reset successfully";
    },
    resetUserPasswordError: (state, action) => {
      state.actionErrors.resetPassword = action.payload;
      state.actionStatus.resettingPassword = false;
    },

    createUserRequest: (state) => {
      state.actionStatus.creating = true;
      state.actionErrors.create = null;
    },
    createUserSuccess: (state) => {
      state.actionStatus.creating = false;
      state.successMessage = "User created successfully";
    },
    createUserError: (state, action) => {
      state.actionErrors.create = action.payload;
      state.actionStatus.creating = false;
    },

    updateUserRequest: (state) => {
      state.actionStatus.updating = true;
      state.actionErrors.update = null;
    },
    updateUserSuccess: (state, action) => {
      if (action.payload) {
        const index = state.usersList.findIndex(
          (u) => u.id === action.payload.id,
        );
        if (index !== -1) {
          state.usersList[index] = action.payload;
        }
      }
      state.actionStatus.updating = false;
      state.successMessage = "User updated successfully";
    },
    updateUserError: (state, action) => {
      state.actionErrors.update = action.payload;
      state.actionStatus.updating = false;
    },

    checkUsernameAvailabilityRequest: (state) => {
      state.actionStatus.checkingUsername = true;
      state.usernameCheck.error = null;
      state.usernameCheck.available = null;
      state.usernameCheck.message = null;
    },
    checkUsernameAvailabilitySuccess: (state, action) => {
      state.actionStatus.checkingUsername = false;
      state.usernameCheck.available = action.payload.available;
      state.usernameCheck.message = action.payload.message;
    },
    checkUsernameAvailabilityError: (state, action) => {
      state.actionStatus.checkingUsername = false;
      state.usernameCheck.error = action.payload;
      state.usernameCheck.available = null;
      state.usernameCheck.message = null;
    },
    clearUsernameCheck: (state) => {
      state.usernameCheck.available = null;
      state.usernameCheck.message = null;
      state.usernameCheck.error = null;
    },

    fetchCurrentUserRequest: (state) => {
      state.actionStatus.fetchingCurrentUser = true;
      state.actionErrors.currentUser = null;
    },
    fetchCurrentUserSuccess: (state, action) => {
      state.actionStatus.fetchingCurrentUser = false;
      state.currentUser = action.payload;
    },
    fetchCurrentUserError: (state, action) => {
      state.actionStatus.fetchingCurrentUser = false;
      state.actionErrors.currentUser = action.payload;
    },

    fetchTemplatesRequest: (state) => {
      state.actionStatus.fetchingTemplates = true;
      state.actionErrors.templates = null;
    },
    fetchTemplatesSuccess: (state, action) => {
      state.actionStatus.fetchingTemplates = false;
      state.templates = action.payload;
    },
    fetchTemplatesError: (state, action) => {
      state.actionStatus.fetchingTemplates = false;
      state.actionErrors.templates = action.payload;
    },

    fetchUserDetailRequest: (state) => {
      state.actionStatus.fetchingUserDetail = true;
      state.actionErrors.userDetail = null;
      state.userDetail = null;
    },
    fetchUserDetailSuccess: (state, action) => {
      state.actionStatus.fetchingUserDetail = false;
      state.userDetail = action.payload;
    },
    fetchUserDetailError: (state, action) => {
      state.actionStatus.fetchingUserDetail = false;
      state.actionErrors.userDetail = action.payload;
    },
    clearUserDetail: (state) => {
      state.userDetail = null;
      state.actionErrors.userDetail = null;
    },

    fetchWorkloadWorkkardsRequest: (state, action) => {
      state.workload.loading = true;
      state.workload.error = null;
      if (action.payload?.userId) {
        state.workload.selectedUser = action.payload.userId;
      }
    },
    fetchWorkloadWorkkardsSuccess: (state, action) => {
      state.workload.workkards = action.payload.items;
      state.workload.workkardsCount = action.payload.totalCount;
      state.workload.loading = false;
    },
    fetchWorkloadWorkkardsError: (state, action) => {
      state.workload.error = action.payload;
      state.workload.loading = false;
    },

    fetchWorkloadActivitiesRequest: (state, action) => {
      state.workload.loading = true;
      state.workload.error = null;
      if (action.payload?.userId) {
        state.workload.selectedUser = action.payload.userId;
      }
    },
    fetchWorkloadActivitiesSuccess: (state, action) => {
      state.workload.activities = action.payload.items;
      state.workload.activitiesCount = action.payload.totalCount;
      state.workload.loading = false;
    },
    fetchWorkloadActivitiesError: (state, action) => {
      state.workload.error = action.payload;
      state.workload.loading = false;
    },

    transferWorkloadRequest: (state) => {
      state.workload.transferring = true;
      state.workload.transferError = null;
    },
    transferWorkloadSuccess: (state, action) => {
      state.workload.transferring = false;
      state.successMessage = action.payload?.message || "Workload transferred successfully";
    },
    transferWorkloadError: (state, action) => {
      state.workload.transferError = action.payload;
      state.workload.transferring = false;
    },

    setWorkloadActiveTab: (state, action) => {
      state.workload.activeTab = action.payload;
    },

    setWorkloadSelectedUser: (state, action) => {
      state.workload.selectedUser = action.payload;
    },

    clearWorkload: (state) => {
      state.workload.workkards = [];
      state.workload.workkardsCount = 0;
      state.workload.activities = [];
      state.workload.activitiesCount = 0;
      state.workload.selectedUser = null;
      state.workload.activeTab = 'workkards';
      state.workload.loading = false;
      state.workload.transferring = false;
      state.workload.error = null;
      state.workload.transferError = null;
    },
  },
});

export const {
  fetchUsersListRequest,
  fetchUsersListSuccess,
  fetchUsersListError,
  fetchSupervisorsListRequest,
  fetchSupervisorsListSuccess,
  fetchSupervisorsListError,
  resetSupervisorsList,
  setSearchTerm,
  setActiveFilter,
  clearFilters,
  clearError,
  clearSuccessMessage,
  blockUserRequest,
  blockUserSuccess,
  blockUserError,
  unblockUserRequest,
  unblockUserSuccess,
  unblockUserError,
  resetUserPasswordRequest,
  resetUserPasswordSuccess,
  resetUserPasswordError,
  createUserRequest,
  createUserSuccess,
  createUserError,
  updateUserRequest,
  updateUserSuccess,
  updateUserError,
  checkUsernameAvailabilityRequest,
  checkUsernameAvailabilitySuccess,
  checkUsernameAvailabilityError,
  clearUsernameCheck,
  fetchCurrentUserRequest,
  fetchCurrentUserSuccess,
  fetchCurrentUserError,
  fetchTemplatesRequest,
  fetchTemplatesSuccess,
  fetchTemplatesError,
  fetchUserDetailRequest,
  fetchUserDetailSuccess,
  fetchUserDetailError,
  clearUserDetail,
  fetchWorkloadWorkkardsRequest,
  fetchWorkloadWorkkardsSuccess,
  fetchWorkloadWorkkardsError,
  fetchWorkloadActivitiesRequest,
  fetchWorkloadActivitiesSuccess,
  fetchWorkloadActivitiesError,
  transferWorkloadRequest,
  transferWorkloadSuccess,
  transferWorkloadError,
  setWorkloadActiveTab,
  setWorkloadSelectedUser,
  clearWorkload,
} = usersSlice.actions;

export default usersSlice.reducer;
